import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Crown, Check, Building2, Users, Calendar, AlertTriangle, 
  CreditCard, Zap, Clock, HelpCircle, ChevronDown, Shield,
  TrendingUp, RefreshCw, X
} from 'lucide-react';
import { toast } from 'sonner';
import AppHeader from '@/components/shared/AppHeader';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PLAN_HIERARCHY = { starter: 1, professional: 2, enterprise: 3 };

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(amount);
};

export default function SubscriptionPage({ user, onLogout }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [processing, setProcessing] = useState(false);
  
  // Dynamic pricing from API
  const [planConfig, setPlanConfig] = useState({
    starter: { monthly: { amount: 999, locationsLimit: 3, therapistsLimit: 10 }, annual: { amount: 9990, locationsLimit: 3, therapistsLimit: 10 } },
    professional: { monthly: { amount: 2499, locationsLimit: 10, therapistsLimit: 50 }, annual: { amount: 24990, locationsLimit: 10, therapistsLimit: 50 } },
    enterprise: { monthly: { amount: 7999, locationsLimit: -1, therapistsLimit: -1 }, annual: { amount: 79990, locationsLimit: -1, therapistsLimit: -1 } }
  });
  const [pricingConfig, setPricingConfig] = useState({ trial_days: 5, annual_discount: 17 });
  
  // Change Plan Modal State
  const [showChangePlanModal, setShowChangePlanModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('starter');
  const [selectedBillingCycle, setSelectedBillingCycle] = useState('monthly');
  const [autoRenewal, setAutoRenewal] = useState(true);
  const [downgradeError, setDowngradeError] = useState(null);
  
  // Cancel Modal State
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch subscription and plans in parallel
      const [subRes, plansRes] = await Promise.all([
        fetch(`${API_URL}/api/tenant/my-subscription`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/tenant/plans`)
      ]);
      
      if (subRes.ok) {
        const data = await subRes.json();
        setSubscription(data);
        
        // Set default selection to current plan
        const currentPlanBase = data.plan?.replace('_monthly', '').replace('_annual', '').replace('_yearly', '') || 'starter';
        const currentBillingCycle = data.billing_cycle || 'monthly';
        setSelectedPlan(currentPlanBase);
        setSelectedBillingCycle(currentBillingCycle);
        setAutoRenewal(data.auto_renewal || false);
      }
      
      if (plansRes.ok) {
        const plansData = await plansRes.json();
        setPricingConfig({
          trial_days: plansData.trial_days || 5,
          annual_discount: plansData.annual_discount || 17
        });
        
        // Convert plans array to config format
        const newConfig = {
          starter: { monthly: {}, annual: {} },
          professional: { monthly: {}, annual: {} },
          enterprise: { monthly: {}, annual: {} }
        };
        
        for (const plan of plansData.plans || []) {
          // Extract tier from plan id (e.g., "starter_monthly" -> "starter")
          const tier = plan.id.replace('_monthly', '').replace('_yearly', '').replace('_annual', '');
          const cycle = plan.billing_cycle === 'yearly' ? 'annual' : plan.billing_cycle;
          
          if (newConfig[tier] && newConfig[tier][cycle] !== undefined) {
            newConfig[tier][cycle] = {
              amount: plan.amount,
              original_amount: plan.original_amount,
              locationsLimit: plan.max_properties,
              therapistsLimit: plan.max_therapists
            };
          }
        }
        
        setPlanConfig(newConfig);
      }
    } catch (error) {
      toast.error('Failed to load subscription data');
    } finally {
      setLoading(false);
    }
  };

  // ========== HELPER FUNCTIONS ==========
  
  // Check if a plan matches current subscription (BOTH name AND billing cycle must match)
  const isCurrentPlan = (planName, billingCycle) => {
    if (!subscription || subscription.is_trial) return false;
    const currentPlanBase = subscription.plan?.replace('_monthly', '').replace('_annual', '');
    const currentBillingCycle = subscription.billing_cycle;
    return currentPlanBase === planName && currentBillingCycle === billingCycle;
  };

  // Check if downgrade is blocked due to usage exceeding limits
  const checkDowngradeBlocked = (planName) => {
    if (!subscription?.usage) return { blocked: false, message: null };
    
    const limits = planConfig[planName]?.[selectedBillingCycle];
    if (!limits) return { blocked: false, message: null };
    
    const currentLocations = subscription.usage.locations || 0;
    const currentTherapists = subscription.usage.therapists || 0;
    
    // Unlimited plan never blocked
    if (limits.locationsLimit === -1) return { blocked: false, message: null };
    
    const issues = [];
    if (currentLocations > limits.locationsLimit) {
      const excess = currentLocations - limits.locationsLimit;
      issues.push(`Archive ${excess} location${excess > 1 ? 's' : ''}`);
    }
    if (currentTherapists > limits.therapistsLimit) {
      const excess = currentTherapists - limits.therapistsLimit;
      issues.push(`Archive ${excess} therapist${excess > 1 ? 's' : ''}`);
    }
    
    if (issues.length > 0) {
      return { 
        blocked: true, 
        message: `${issues.join(' and ')} to select this plan (Current: ${currentLocations} locations, ${currentTherapists} therapists)`
      };
    }
    return { blocked: false, message: null };
  };

  // Calculate what happens with selected plan
  const getSelectedPlanInfo = () => {
    const config = planConfig[selectedPlan]?.[selectedBillingCycle];
    if (!config) return null;
    
    const planId = `${selectedPlan}_${selectedBillingCycle}`;
    const daysToAdd = selectedBillingCycle === 'annual' ? 365 : 30;
    const isSamePlan = isCurrentPlan(selectedPlan, selectedBillingCycle);
    
    // Check if this is an upgrade or downgrade
    const currentPlanBase = subscription?.plan?.replace('_monthly', '').replace('_annual', '') || 'starter';
    const currentLevel = PLAN_HIERARCHY[currentPlanBase] || 1;
    const newLevel = PLAN_HIERARCHY[selectedPlan] || 1;
    
    let action = 'subscribe';
    if (subscription && !subscription.is_trial && !subscription.is_expired) {
      if (isSamePlan) {
        action = 'renew'; // Same plan + same cycle = extend days
      } else if (newLevel > currentLevel) {
        action = 'upgrade';
      } else if (newLevel < currentLevel) {
        action = 'downgrade';
      } else {
        action = 'change_cycle'; // Same plan level, different cycle
      }
    }
    
    return {
      planId,
      amount: config.amount,
      daysToAdd,
      locationsLimit: config.locationsLimit,
      therapistsLimit: config.therapistsLimit,
      action,
      isSamePlan
    };
  };

  // ========== PAYMENT HANDLERS ==========
  
  const loadRazorpayScript = () => {
    return new Promise((resolve, reject) => {
      if (window.Razorpay) {
        resolve();
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = resolve;
      script.onerror = reject;
      document.body.appendChild(script);
    });
  };

  const handleChangePlan = async () => {
    const planInfo = getSelectedPlanInfo();
    if (!planInfo) return;
    
    // Check for downgrade restriction
    const downgradeCheck = checkDowngradeBlocked(selectedPlan);
    if (downgradeCheck.blocked) {
      setDowngradeError(downgradeCheck.message);
      return;
    }
    
    setProcessing(true);
    setDowngradeError(null);
    
    try {
      const token = localStorage.getItem('token');
      
      if (autoRenewal) {
        // Auto-renewal: Use Razorpay Subscriptions API
        await handleAutoRenewalPayment(planInfo, token);
      } else {
        // One-time: Use Razorpay Orders API
        await handleOneTimePayment(planInfo, token);
      }
    } catch (error) {
      toast.error(error.message || 'Failed to process payment');
      console.error(error);
    } finally {
      setProcessing(false);
    }
  };

  const handleAutoRenewalPayment = async (planInfo, token) => {
    const res = await fetch(`${API_URL}/api/tenant/create-subscription-checkout`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        plan_id: planInfo.planId,
        billing_cycle: selectedBillingCycle,
        auto_renewal: true
      })
    });

    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Failed to create subscription');
    }

    const subData = await res.json();
    await loadRazorpayScript();

    if (!subData.razorpay_key || !subData.subscription_id) {
      throw new Error('Payment gateway configuration error. Please try one-time payment.');
    }

    const options = {
      key: subData.razorpay_key,
      subscription_id: subData.subscription_id,
      name: 'WellNest Technologies',
      description: `${selectedPlan.charAt(0).toUpperCase() + selectedPlan.slice(1)} - ${selectedBillingCycle.charAt(0).toUpperCase() + selectedBillingCycle.slice(1)} (Auto-Renewal)`,
      handler: async (response) => {
        await verifySubscriptionPayment(response, planInfo.planId);
      },
      prefill: {
        email: user?.email || subscription?.admin_email,
        contact: subscription?.admin_phone || ''
      },
      theme: { color: '#0d9488' }
    };

    const rzp = new window.Razorpay(options);
    rzp.open();
    setShowChangePlanModal(false);
  };

  const handleOneTimePayment = async (planInfo, token) => {
    // Use renew-subscription endpoint for one-time payments
    const res = await fetch(`${API_URL}/api/tenant/renew-subscription`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        plan_id: planInfo.planId,
        amount: planInfo.amount,
        auto_renewal: false
      })
    });

    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Failed to create payment order');
    }

    const orderData = await res.json();
    await loadRazorpayScript();

    if (!orderData.razorpay_key) {
      throw new Error('Payment gateway key not configured.');
    }

    const options = {
      key: orderData.razorpay_key,
      amount: orderData.amount_paise,
      currency: orderData.currency || 'INR',
      name: 'WellNest Technologies',
      description: `${selectedPlan.charAt(0).toUpperCase() + selectedPlan.slice(1)} - ${selectedBillingCycle.charAt(0).toUpperCase() + selectedBillingCycle.slice(1)}`,
      order_id: orderData.order_id,
      handler: async (response) => {
        await verifyRenewalPayment(response, planInfo.planId, planInfo.daysToAdd);
      },
      prefill: {
        email: user?.email || subscription?.admin_email,
        contact: subscription?.admin_phone || ''
      },
      theme: { color: '#0d9488' }
    };

    const rzp = new window.Razorpay(options);
    rzp.open();
    setShowChangePlanModal(false);
  };

  const verifySubscriptionPayment = async (response, planId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/tenant/verify-subscription-payment`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          razorpay_subscription_id: response.razorpay_subscription_id,
          razorpay_payment_id: response.razorpay_payment_id,
          razorpay_signature: response.razorpay_signature,
          plan_id: planId
        })
      });

      if (res.ok) {
        toast.success('Subscription activated successfully!');
        localStorage.removeItem('subscription_status');
        fetchSubscription();
      } else {
        toast.error('Payment verification failed. Please contact support.');
      }
    } catch (error) {
      toast.error('Payment verification error.');
    }
  };

  const verifyRenewalPayment = async (response, planId, daysToAdd) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/tenant/verify-renewal`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          razorpay_order_id: response.razorpay_order_id,
          razorpay_payment_id: response.razorpay_payment_id,
          razorpay_signature: response.razorpay_signature,
          plan_id: planId
        })
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(`Payment successful! ${data.days_added || daysToAdd} days added.`);
        localStorage.removeItem('subscription_status');
        fetchSubscription();
      } else {
        toast.error('Payment verification failed. Please contact support.');
      }
    } catch (error) {
      toast.error('Payment verification error.');
    }
  };

  // ========== CANCEL SUBSCRIPTION ==========
  
  const handleCancelSubscription = async () => {
    setCancelling(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/tenant/cancel-subscription`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason: cancelReason })
      });

      const data = await res.json();
      
      if (res.ok) {
        toast.success('Auto-renewal turned off. Your subscription remains active until expiry.');
        setShowCancelModal(false);
        setCancelReason('');
        fetchSubscription();
      } else {
        toast.error(data.detail || 'Failed to cancel subscription');
      }
    } catch (error) {
      toast.error('Failed to cancel subscription');
    } finally {
      setCancelling(false);
    }
  };

  const handleReactivateSubscription = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/tenant/reactivate-subscription`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });

      if (res.ok) {
        toast.success('Auto-renewal reactivated!');
        fetchSubscription();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to reactivate');
      }
    } catch (error) {
      toast.error('Failed to reactivate subscription');
    }
  };

  // ========== RENDER ==========
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-teal-400 animate-spin" />
      </div>
    );
  }

  const isTrial = subscription?.is_trial;
  const isExpired = subscription?.is_expired;
  const hasAutoRenewal = subscription?.auto_renewal && !subscription?.cancel_at_period_end;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      <AppHeader user={user} onLogout={onLogout} showBack backTo="/admin" title="My Plan" />
      
      <main className="max-w-5xl mx-auto px-4 py-8">
        
        {/* ========== SECTION 1: CURRENT SUBSCRIPTION STATUS ========== */}
        <Card className={`mb-8 ${
          isExpired 
            ? 'bg-red-500/10 border-red-500/30'
            : isTrial
            ? 'bg-amber-500/10 border-amber-500/30'
            : 'bg-gradient-to-br from-teal-500/10 to-cyan-500/10 border-teal-500/30'
        }`} data-testid="current-subscription-card">
          <CardContent className="p-6">
            <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-6">
              
              {/* Left: Plan Info */}
              <div className="flex items-start gap-4">
                <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                  isExpired ? 'bg-red-500/20' : isTrial ? 'bg-amber-500/20' : 'bg-teal-500/20'
                }`}>
                  {isExpired ? (
                    <AlertTriangle className="w-7 h-7 text-red-400" />
                  ) : isTrial ? (
                    <Clock className="w-7 h-7 text-amber-400" />
                  ) : (
                    <Crown className="w-7 h-7 text-teal-400" />
                  )}
                </div>
                <div>
                  {/* Plan Name + Billing Cycle */}
                  <div className="flex items-center gap-3 flex-wrap">
                    <h2 className="text-xl font-bold text-white" data-testid="current-plan-name">
                      {isExpired 
                        ? 'Subscription Expired'
                        : isTrial
                        ? 'Free Trial'
                        : `${subscription?.plan_name || 'Active'} (${subscription?.billing_cycle?.charAt(0).toUpperCase() + subscription?.billing_cycle?.slice(1) || 'Monthly'})`}
                    </h2>
                    {!isTrial && !isExpired && (
                      <Badge className={hasAutoRenewal ? 'bg-teal-500/20 text-teal-400' : 'bg-slate-700 text-slate-300'}>
                        {hasAutoRenewal ? (
                          <><Zap className="w-3 h-3 mr-1" /> Auto-Renewal ON</>
                        ) : subscription?.cancel_at_period_end ? (
                          <><AlertTriangle className="w-3 h-3 mr-1" /> Cancelling</>
                        ) : (
                          'Manual Renewal'
                        )}
                      </Badge>
                    )}
                  </div>
                  
                  {/* Expiry Info */}
                  <p className="text-slate-400 mt-1" data-testid="expiry-info">
                    {isExpired 
                      ? 'Your subscription has expired. Please renew to continue.'
                      : subscription?.cancel_at_period_end
                      ? `Access ends ${new Date(subscription?.ends_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}. Auto-renewal is OFF.`
                      : hasAutoRenewal
                      ? `Auto-renews ${new Date(subscription?.ends_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}`
                      : `Expires ${new Date(subscription?.ends_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}`}
                  </p>
                  
                  {/* Days Remaining */}
                  {!isExpired && (
                    <div className="flex items-center gap-4 mt-3 flex-wrap">
                      <span className={`text-sm ${subscription?.days_remaining <= 7 ? 'text-amber-400' : 'text-slate-400'}`}>
                        <Clock className="w-4 h-4 inline mr-1" />
                        {subscription?.days_remaining} days remaining
                      </span>
                    </div>
                  )}
                  
                  {/* Action Buttons - Per Spec */}
                  <div className="flex items-center gap-3 mt-4 flex-wrap">
                    {/* Trial Users: Show "Subscribe Now" only (Hide Cancel/Renew) */}
                    {isTrial && (
                      <Button
                        onClick={() => setShowChangePlanModal(true)}
                        className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600"
                        data-testid="subscribe-btn"
                      >
                        <CreditCard className="w-4 h-4 mr-2" />
                        Subscribe Now
                      </Button>
                    )}
                    
                    {/* Expired Users: Show "Renew Now" */}
                    {isExpired && (
                      <Button
                        onClick={() => setShowChangePlanModal(true)}
                        className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600"
                        data-testid="renew-btn"
                      >
                        <CreditCard className="w-4 h-4 mr-2" />
                        Renew Now
                      </Button>
                    )}
                    
                    {/* Active Subscribed Users (NOT trial, NOT expired) */}
                    {!isTrial && !isExpired && (
                      <>
                        {/* Change Plan Button - For manual renewal OR to extend/upgrade/downgrade */}
                        <Button
                          onClick={() => setShowChangePlanModal(true)}
                          className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600"
                          data-testid="change-plan-btn"
                        >
                          <CreditCard className="w-4 h-4 mr-2" />
                          {hasAutoRenewal ? 'Change Plan' : 'Renew / Change Plan'}
                        </Button>
                        
                        {/* Cancel / Reactivate */}
                        {subscription?.cancel_at_period_end ? (
                          <Button
                            variant="outline"
                            onClick={handleReactivateSubscription}
                            className="border-teal-500/50 text-teal-400 hover:bg-teal-500/10"
                            data-testid="reactivate-btn"
                          >
                            Turn On Auto-Renewal
                          </Button>
                        ) : hasAutoRenewal ? (
                          <Button
                            variant="outline"
                            onClick={() => setShowCancelModal(true)}
                            className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                            data-testid="cancel-btn"
                          >
                            Turn Off Auto-Renewal
                          </Button>
                        ) : null}
                      </>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Right: Usage Stats */}
              <div className="grid grid-cols-2 gap-4 lg:min-w-[280px]">
                <div className="text-center p-4 bg-slate-800/50 rounded-xl" data-testid="locations-usage">
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <Building2 className="w-5 h-5 text-blue-400" />
                    <span className="text-2xl font-bold text-white">
                      {subscription?.usage?.locations || 0}
                    </span>
                    <span className="text-slate-500">
                      / {subscription?.limits?.locations === -1 ? '∞' : subscription?.limits?.locations}
                    </span>
                  </div>
                  <p className="text-xs text-slate-400">Locations</p>
                </div>
                <div className="text-center p-4 bg-slate-800/50 rounded-xl" data-testid="therapists-usage">
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <Users className="w-5 h-5 text-purple-400" />
                    <span className="text-2xl font-bold text-white">
                      {subscription?.usage?.therapists || 0}
                    </span>
                    <span className="text-slate-500">
                      / {subscription?.limits?.therapists === -1 ? '∞' : subscription?.limits?.therapists}
                    </span>
                  </div>
                  <p className="text-xs text-slate-400">Therapists</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ========== SECTION 2: AVAILABLE PLANS GRID ========== */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-white mb-4">Available Plans</h2>
          <div className="grid md:grid-cols-3 gap-4">
            {['starter', 'professional', 'enterprise'].map((planName) => {
              const monthlyConfig = planConfig[planName].monthly;
              const annualConfig = planConfig[planName].annual;
              const isCurrentMonthly = isCurrentPlan(planName, 'monthly');
              const isCurrentAnnual = isCurrentPlan(planName, 'annual');
              const isCurrent = isCurrentMonthly || isCurrentAnnual;
              
              return (
                <Card 
                  key={planName}
                  className={`bg-slate-800/30 border ${
                    isCurrent ? 'border-teal-500 ring-2 ring-teal-500/30' : 'border-slate-700'
                  }`}
                  data-testid={`plan-card-${planName}`}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-white capitalize">{planName}</CardTitle>
                      {isCurrent && (
                        <Badge className="bg-teal-500 text-white">Current</Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {/* Pricing */}
                    <div className="mb-4">
                      <div className="flex items-baseline gap-2">
                        <span className="text-2xl font-bold text-white">{formatCurrency(monthlyConfig.amount)}</span>
                        <span className="text-slate-400">/month</span>
                      </div>
                      <div className="text-sm text-teal-400">
                        or {formatCurrency(annualConfig.amount)}/year (save 17%)
                      </div>
                    </div>
                    
                    {/* Limits */}
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2 text-slate-300">
                        <Building2 className="w-4 h-4 text-blue-400" />
                        <span>{monthlyConfig.locationsLimit === -1 ? 'Unlimited' : monthlyConfig.locationsLimit} Locations</span>
                      </div>
                      <div className="flex items-center gap-2 text-slate-300">
                        <Users className="w-4 h-4 text-purple-400" />
                        <span>{monthlyConfig.therapistsLimit === -1 ? 'Unlimited' : monthlyConfig.therapistsLimit} Therapists</span>
                      </div>
                    </div>
                    
                    {/* Current Plan Indicator */}
                    {isCurrentMonthly && (
                      <p className="text-xs text-teal-400 mt-3">Your current plan (Monthly)</p>
                    )}
                    {isCurrentAnnual && (
                      <p className="text-xs text-teal-400 mt-3">Your current plan (Annual)</p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* ========== SECTION 3: BILLING GUIDE & FAQ ========== */}
        <Card className="bg-slate-800/30 border-slate-700">
          <CardContent className="p-4">
            <details className="group">
              <summary className="flex items-center justify-between cursor-pointer list-none">
                <div className="flex items-center gap-2 text-slate-300">
                  <HelpCircle className="w-5 h-5 text-teal-400" />
                  <span className="font-medium">Terms, Billing Guide & FAQ</span>
                </div>
                <ChevronDown className="w-5 h-5 text-slate-400 group-open:rotate-180 transition-transform" />
              </summary>
              
              <div className="mt-4 space-y-6 text-sm">
                
                {/* Terms & Conditions */}
                <div className="p-4 bg-slate-900/50 rounded-lg border border-slate-600">
                  <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                    <Shield className="w-4 h-4 text-teal-400" />
                    Terms & Conditions
                  </h3>
                  <div className="text-slate-400 space-y-2 text-xs">
                    <p><strong className="text-white">1. No Refunds:</strong> All payments are FINAL and NON-REFUNDABLE. No refunds for unused days, downgrades, or cancellations.</p>
                    <p><strong className="text-white">2. Auto-Renewal:</strong> If enabled, your card is charged automatically on expiry. You can turn it off anytime.</p>
                    <p><strong className="text-white">3. Cancel = Auto-Renewal OFF:</strong> Cancelling only disables auto-renewal. Your access continues until expiry date.</p>
                    <p><strong className="text-white">4. Data Retention:</strong> After 30 days of expiry, operational data may be deleted. Company record is always retained.</p>
                    <p><strong className="text-white">5. Immediate Effect:</strong> All plan changes take effect immediately after payment.</p>
                  </div>
                </div>

                {/* How Billing Works */}
                <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
                  <h3 className="text-teal-400 font-semibold mb-3 flex items-center gap-2">
                    <CreditCard className="w-4 h-4" />
                    How Billing Works
                  </h3>
                  <ul className="space-y-2 text-slate-400 text-xs">
                    <li className="flex gap-2">
                      <Check className="w-4 h-4 text-teal-400 shrink-0" />
                      <span><strong className="text-white">Days are ADDED:</strong> When you renew (same plan + same cycle), new days are added to your current expiry. Example: 5 days left + 30 = 35 days.</span>
                    </li>
                    <li className="flex gap-2">
                      <Check className="w-4 h-4 text-teal-400 shrink-0" />
                      <span><strong className="text-white">Full price:</strong> Each payment is the full price of the selected plan. No proration.</span>
                    </li>
                    <li className="flex gap-2">
                      <Check className="w-4 h-4 text-teal-400 shrink-0" />
                      <span><strong className="text-white">Immediate:</strong> Upgrades/downgrades take effect immediately. New limits apply instantly.</span>
                    </li>
                  </ul>
                </div>

                {/* Upgrading */}
                <div className="p-4 bg-green-500/10 rounded-lg border border-green-500/30">
                  <h3 className="text-green-400 font-semibold mb-2 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" />
                    Upgrading Your Plan
                  </h3>
                  <p className="text-slate-400 text-xs">
                    Click "Change Plan" → Select a higher-tier plan → Pay full price → New limits apply immediately + days added to expiry.
                  </p>
                </div>

                {/* Downgrading */}
                <div className="p-4 bg-amber-500/10 rounded-lg border border-amber-500/30">
                  <h3 className="text-amber-400 font-semibold mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    Downgrading Your Plan
                  </h3>
                  <p className="text-slate-400 text-xs">
                    <strong className="text-white">You cannot downgrade if your current usage exceeds the new plan's limits.</strong> First archive excess locations/therapists, then select the lower plan.
                  </p>
                </div>

                {/* FAQ */}
                <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
                  <h3 className="text-teal-400 font-semibold mb-3">FAQ</h3>
                  <div className="space-y-3 text-xs">
                    <div>
                      <p className="text-white font-medium">Q: What does "Cancel" actually do?</p>
                      <p className="text-slate-400">A: It turns OFF auto-renewal. Your subscription stays active until the expiry date. No refund.</p>
                    </div>
                    <div>
                      <p className="text-white font-medium">Q: Can I get a refund?</p>
                      <p className="text-slate-400">A: No. All payments are final and non-refundable.</p>
                    </div>
                    <div>
                      <p className="text-white font-medium">Q: What happens when my subscription expires?</p>
                      <p className="text-slate-400">A: Staff are logged out. Only admin can access this page to renew. Data is kept for 30 days.</p>
                    </div>
                    <div>
                      <p className="text-white font-medium">Q: Do archived items count against limits?</p>
                      <p className="text-slate-400">A: No. Only active items count toward your plan limits.</p>
                    </div>
                  </div>
                </div>

                <p className="text-center text-slate-500 text-xs">
                  Questions? Contact <a href="mailto:support@wellnesttechnologies.com" className="text-teal-400 hover:underline">support@wellnesttechnologies.com</a>
                </p>
              </div>
            </details>
          </CardContent>
        </Card>
      </main>

      {/* ========== CHANGE PLAN MODAL ========== */}
      {showChangePlanModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <Card className="bg-slate-900 border-slate-700 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <CardHeader className="border-b border-slate-700">
              <div className="flex items-center justify-between">
                <CardTitle className="text-white">
                  {isTrial ? 'Subscribe' : isExpired ? 'Renew Subscription' : 'Change Plan'}
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowChangePlanModal(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              
              {/* Current Usage Reminder */}
              {subscription?.usage && (
                <div className="p-3 bg-slate-800/50 rounded-lg text-sm">
                  <p className="text-slate-400">
                    Current Usage: <span className="text-white">{subscription.usage.locations} locations</span>, <span className="text-white">{subscription.usage.therapists} therapists</span>
                  </p>
                </div>
              )}
              
              {/* Plan Selection */}
              <div>
                <label className="text-sm text-slate-400 block mb-2">Select Plan</label>
                <div className="grid grid-cols-3 gap-2">
                  {['starter', 'professional', 'enterprise'].map((plan) => {
                    const config = planConfig[plan][selectedBillingCycle];
                    const downgradeCheck = checkDowngradeBlocked(plan);
                    const isSelected = selectedPlan === plan;
                    
                    return (
                      <button
                        key={plan}
                        onClick={() => {
                          setSelectedPlan(plan);
                          setDowngradeError(null);
                        }}
                        disabled={downgradeCheck.blocked}
                        className={`p-3 rounded-lg border text-left transition-all ${
                          downgradeCheck.blocked
                            ? 'border-red-500/30 bg-red-500/5 cursor-not-allowed opacity-60'
                            : isSelected
                            ? 'border-teal-500 bg-teal-500/10'
                            : 'border-slate-700 hover:border-slate-600'
                        }`}
                        data-testid={`modal-plan-${plan}`}
                      >
                        <p className="text-white font-medium capitalize">{plan}</p>
                        <p className="text-xs text-slate-400 mt-1">
                          {config.locationsLimit === -1 ? '∞' : config.locationsLimit} loc / {config.therapistsLimit === -1 ? '∞' : config.therapistsLimit} staff
                        </p>
                        {downgradeCheck.blocked && (
                          <Badge className="mt-2 bg-red-500/20 text-red-400 text-xs">Blocked</Badge>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
              
              {/* Billing Cycle Selection */}
              <div>
                <label className="text-sm text-slate-400 block mb-2">Billing Cycle</label>
                <div className="grid grid-cols-2 gap-2">
                  {['monthly', 'annual'].map((cycle) => {
                    const config = planConfig[selectedPlan][cycle];
                    const isSelected = selectedBillingCycle === cycle;
                    
                    return (
                      <button
                        key={cycle}
                        onClick={() => setSelectedBillingCycle(cycle)}
                        className={`p-3 rounded-lg border text-left transition-all ${
                          isSelected
                            ? 'border-teal-500 bg-teal-500/10'
                            : 'border-slate-700 hover:border-slate-600'
                        }`}
                        data-testid={`modal-cycle-${cycle}`}
                      >
                        <p className="text-white font-medium capitalize">{cycle}</p>
                        <p className="text-lg text-teal-400 font-bold">{formatCurrency(config.amount)}</p>
                        <p className="text-xs text-slate-400">{cycle === 'annual' ? '365 days' : '30 days'}</p>
                      </button>
                    );
                  })}
                </div>
              </div>
              
              {/* Payment Type Selection */}
              <div>
                <label className="text-sm text-slate-400 block mb-2">Payment Type</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setAutoRenewal(true)}
                    className={`p-3 rounded-lg border text-left transition-all ${
                      autoRenewal
                        ? 'border-teal-500 bg-teal-500/10'
                        : 'border-slate-700 hover:border-slate-600'
                    }`}
                    data-testid="modal-auto-renewal"
                  >
                    <div className="flex items-center gap-2">
                      <Zap className="w-4 h-4 text-teal-400" />
                      <p className="text-white font-medium">Auto-Renewal</p>
                    </div>
                    <p className="text-xs text-slate-400 mt-1">Card saved. Auto-charged on expiry.</p>
                  </button>
                  <button
                    onClick={() => setAutoRenewal(false)}
                    className={`p-3 rounded-lg border text-left transition-all ${
                      !autoRenewal
                        ? 'border-teal-500 bg-teal-500/10'
                        : 'border-slate-700 hover:border-slate-600'
                    }`}
                    data-testid="modal-one-time"
                  >
                    <div className="flex items-center gap-2">
                      <CreditCard className="w-4 h-4 text-slate-400" />
                      <p className="text-white font-medium">One-Time</p>
                    </div>
                    <p className="text-xs text-slate-400 mt-1">Manual renewal required.</p>
                  </button>
                </div>
              </div>
              
              {/* Summary */}
              {(() => {
                const planInfo = getSelectedPlanInfo();
                if (!planInfo) return null;
                
                return (
                  <div className="p-4 bg-slate-800/50 rounded-lg">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-slate-400">Plan</span>
                      <span className="text-white font-medium capitalize">{selectedPlan} ({selectedBillingCycle})</span>
                    </div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-slate-400">Amount</span>
                      <span className="text-xl text-teal-400 font-bold">{formatCurrency(planInfo.amount)}</span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-slate-400">Days Added</span>
                      <span className="text-white">+{planInfo.daysToAdd} days</span>
                    </div>
                    {planInfo.action !== 'subscribe' && !isExpired && (
                      <div className="mt-3 pt-3 border-t border-slate-700">
                        <p className="text-xs text-slate-400">
                          {planInfo.isSamePlan 
                            ? 'Renewing same plan: Days will be ADDED to current expiry.'
                            : `Plan change: New limits apply immediately + ${planInfo.daysToAdd} days added.`}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })()}
              
              {/* Downgrade Error */}
              {downgradeError && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <p className="text-red-400 text-sm flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                    {downgradeError}
                  </p>
                </div>
              )}
              
              {/* Pay Button */}
              <Button
                onClick={handleChangePlan}
                disabled={processing || checkDowngradeBlocked(selectedPlan).blocked}
                className="w-full bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 h-12"
                data-testid="modal-pay-btn"
              >
                {processing ? (
                  <RefreshCw className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <CreditCard className="w-5 h-5 mr-2" />
                    Pay {formatCurrency(planConfig[selectedPlan][selectedBillingCycle].amount)}
                  </>
                )}
              </Button>
              
              <p className="text-center text-xs text-slate-500">
                Secure payment powered by Razorpay
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ========== CANCEL MODAL ========== */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <Card className="bg-slate-900 border-slate-700 max-w-md w-full">
            <CardHeader className="border-b border-slate-700">
              <CardTitle className="text-white flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-400" />
                Turn Off Auto-Renewal?
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <p className="text-amber-400 text-sm">
                  <strong>What this does:</strong> Auto-renewal will be turned off. Your subscription remains active until <strong>{new Date(subscription?.ends_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</strong>.
                </p>
              </div>
              
              <div className="text-sm text-slate-400 space-y-1">
                <p>• No future charges to your card</p>
                <p>• All features continue until expiry</p>
                <p>• You can renew manually anytime</p>
                <p>• You can turn auto-renewal back on</p>
              </div>
              
              <div>
                <label className="text-sm text-slate-400 block mb-2">Reason (optional)</label>
                <select
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  className="w-full p-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
                >
                  <option value="">Select a reason...</option>
                  <option value="too_expensive">Too expensive</option>
                  <option value="not_using">Not using the features</option>
                  <option value="switching">Switching to another service</option>
                  <option value="temporary">Temporary pause</option>
                  <option value="other">Other</option>
                </select>
              </div>
              
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowCancelModal(false)}
                  className="flex-1"
                >
                  Keep Auto-Renewal
                </Button>
                <Button
                  onClick={handleCancelSubscription}
                  disabled={cancelling}
                  className="flex-1 bg-red-600 hover:bg-red-700"
                  data-testid="confirm-cancel-btn"
                >
                  {cancelling ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Turn Off'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
